import React, { useState, useEffect, useMemo } from 'react';
import { IAppointmentDetails } from 'shared/interfaces/IAppointmentDetails';
import AppointmentDetails from './components/AppointmentDetails';
import BookingConfirmation from './components/BookingConfirmation';
import Calendar from './components/Calendar';
import TimeSlotList from './components/TimeSlots';
import {
  useSyncAvailabilitiesMutation,
  PimsIntegrationCapability,
  ClinicPimsIntegrationType,
} from 'shared/types/graphql';
import * as Sentry from '@sentry/react';
import { useWidgetStore } from 'state/useWidgetStore';
import { ClinicWidgetSettingResponse } from 'shared/types';
import CreateAccount from './components/CreateAccount';
import { DirectBookingStepList } from 'state/types/steps';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { Text } from '@televet/kibble-ui/build/components/Text';
import LogRocket from 'logrocket';
import { env } from 'env';
import { appInfo } from 'shared/providers/Tracking';

interface IDirectBookingProps {
  widgetSetting: ClinicWidgetSettingResponse;
}

const isProduction = env.REACT_APP_PRODUCTION === 'true';

const DirectBooking = ({ widgetSetting }: IDirectBookingProps): JSX.Element | null => {
  const {
    clinicPetParent,
    currentStepIndex,
    requestSteps,
    clinicId,
    clinicName,
    isPetPortal,
    authentication: { isAuthenticating },
  } = useWidgetStore((state) => state);
  const [isLRInitialized, setIsLRInitialized] = useState(false);
  const [appointmentDetails, setAppointmentDetails] = useState<IAppointmentDetails>();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const [syncAvailabilities] = useSyncAvailabilitiesMutation({
    variables: {
      data: {
        clinicId: clinicId,
        models: [PimsIntegrationCapability.AppointmentAvailability],
        integrationTypes: [ClinicPimsIntegrationType.Bitwerx],
      },
    },
  });

  const integrations = useMemo(() => widgetSetting?.clinic[0].integrations, [widgetSetting?.clinic]);

  useEffect(() => {
    if (isLRInitialized) return;
    if (isProduction) {
      LogRocket.init(env.REACT_APP_LOG_ROCKET_APP_ID, {
        mergeIframes: true,
        childDomains: ['*'],
        release: `${appInfo.version}`,
      });
      setIsLRInitialized(true);
    }
  }, [isLRInitialized]);

  // To lower cost, we only trigger LR tracking for direct booking users
  useEffect(() => {
    if (!isLRInitialized) return;
    const userData = {
      name: [clinicPetParent?.firstName || '', clinicPetParent?.lastName || ''].join(' ').trim(),
      id: clinicPetParent?.id || '',
      clinicId: clinicId || '',
      clinicName: clinicName || '',
      email: clinicPetParent?.email || 'No email provided',
      environment: env.REACT_APP_PRODUCTION === 'true' ? 'production' : 'development',
      isPetPortal,
    };

    LogRocket.identify(userData.id, userData);
    LogRocket.track('SessionStarted', userData);
    console.log('LogRocket: SessionStarted', { userData });
  }, [
    clinicPetParent?.email,
    clinicPetParent?.firstName,
    clinicPetParent?.id,
    clinicPetParent?.lastName,
    widgetSetting.clinic,
    clinicId,
    clinicName,
    isLRInitialized,
    isPetPortal,
  ]);

  useEffect(() => {
    try {
      if (integrations.length) {
        syncAvailabilities();
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }, [integrations.length, syncAvailabilities]);

  if (isAuthenticating) {
    return <Spinner isFullScreen />;
  }

  switch (requestSteps[currentStepIndex]) {
    case DirectBookingStepList.CreateAccount:
      return <CreateAccount integrations={integrations} />;

    case DirectBookingStepList.AppointmentDetails:
      return (
        <AppointmentDetails
          setAppointmentDetails={setAppointmentDetails}
          appointmentDetails={appointmentDetails}
          integrations={integrations}
        />
      );
    case DirectBookingStepList.Calendar:
      return (
        <Calendar
          setAppointmentDetails={setAppointmentDetails}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          appointmentDetails={appointmentDetails}
        />
      );
    case DirectBookingStepList.Times:
      return (
        <TimeSlotList
          selectedDate={selectedDate}
          setAppointmentDetails={setAppointmentDetails}
          appointmentDetails={appointmentDetails}
        />
      );
    case DirectBookingStepList.PreConfirmation:
    case DirectBookingStepList.BookingConfirmation:
      return (
        <BookingConfirmation
          setAppointmentDetails={setAppointmentDetails}
          appointmentDetails={appointmentDetails}
          integrations={integrations}
        />
      );
    default:
      return <Text>Sorry, this step is not available</Text>;
  }
};

export default DirectBooking;
